import React, { useEffect, useState, useCallback } from 'react';
import { useTransition, animated } from '@react-spring/web';
import styled from 'styled-components';
import PropTypes from 'prop-types';

const NotificationWrapper = styled.div`
  position: fixed;
  top: 7.5rem;
  left: 45%;
  z-index: 1000;
  opacity: 0.8;
`;

const NotificationItem = styled(animated.div)`
  padding: 9px 1rem;
  background-color: #f2f2ec;
  font-size: 15px;
  color: #3C210E;
  border-radius: 5px;
  margin-bottom: 0.5rem;
  box-shadow: 0 5px 10px darkkhaki;
  height: 40px;
  cursor: pointer;
`;

const NotificationHub = ({ notifications, onNotificationDismiss, duration = 3000 }) => {
  const [visibleNotifications, setVisibleNotifications] = useState(notifications);

  useEffect(() => {
    setVisibleNotifications(notifications);
  }, [notifications]);

  const dismissNotification = useCallback((id) => {
    setVisibleNotifications((prevNotifications) =>
      prevNotifications.filter((notification) => notification.id !== id)
    );
    onNotificationDismiss(id);
  }, [onNotificationDismiss]);

  useEffect(() => {
    const timers = visibleNotifications.map(notification => {
      return setTimeout(() => {
        dismissNotification(notification.id);
      }, duration);
    });

    return () => timers.forEach(timer => clearTimeout(timer));
  }, [visibleNotifications, duration, dismissNotification]);

  const transitions = useTransition(visibleNotifications, {
    keys: notification => notification.id,
    from: { opacity: 0, transform: 'translate3d(0, -20px, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, -20px, 0)' },
  });

  return (
    <NotificationWrapper>
      {transitions((style, notification) =>
        notification ? (
          <NotificationItem
            key={notification.id}
            style={style}
            onClick={() => dismissNotification(notification.id)}
          >
            {notification.msgText}
          </NotificationItem>
        ) : null
      )}
    </NotificationWrapper>
  );
};

NotificationHub.propTypes = {
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      msgText: PropTypes.string.isRequired,
    })
  ).isRequired,
  onNotificationDismiss: PropTypes.func.isRequired,
  duration: PropTypes.number,
};

export default NotificationHub;
